import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

function IconClock() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor"
      strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10"/>
      <polyline points="12 6 12 12 16 14"/>
    </svg>
  );
}

function IconPlay() {
  return (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor">
      <polygon points="5 3 19 12 5 21 5 3"/>
    </svg>
  );
}

function IconRefresh() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor"
      strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="23 4 23 10 17 10"/>
      <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"/>
    </svg>
  );
}

const STATUS_COLORS = {
  success: 'var(--success)',
  failed:  'var(--danger)',
  running: 'var(--warning)',
  pending: 'var(--text-muted)',
};

function StatusBadge({ status }) {
  const color = STATUS_COLORS[status] || 'var(--text-muted)';
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 5,
      fontSize: 12, fontWeight: 600,
      padding: '2px 8px', borderRadius: 999,
      background: `${color}22`, color,
      textTransform: 'capitalize',
    }}>
      <span style={{
        width: 6, height: 6, borderRadius: '50%',
        background: color,
        animation: status === 'running' ? 'pulse 1.5s ease-in-out infinite' : 'none',
      }} />
      {status || '—'}
    </span>
  );
}

function ToggleSwitch({ enabled, onChange, disabled }) {
  return (
    <button
      onClick={onChange}
      disabled={disabled}
      style={{
        width: 40, height: 22, borderRadius: 999, padding: 2,
        background: enabled ? 'var(--accent)' : 'var(--border)',
        border: 'none', cursor: disabled ? 'not-allowed' : 'pointer',
        transition: 'background 200ms', display: 'flex', alignItems: 'center',
        opacity: disabled ? 0.5 : 1,
      }}
    >
      <span style={{
        width: 18, height: 18, borderRadius: '50%',
        background: '#fff',
        transform: enabled ? 'translateX(18px)' : 'translateX(0)',
        transition: 'transform 200ms',
        display: 'block',
      }} />
    </button>
  );
}

function fmtDate(val) {
  if (!val) return '—';
  try { return new Date(val).toLocaleString(); } catch { return val; }
}

function fmtCron(expr) {
  const labels = {
    '0 9 * * 1':  'Weekly (Mon 09:00)',
    '0 0 * * *':  'Daily (midnight)',
    '0 * * * *':  'Hourly',
    '* * * * *':  'Every minute',
  };
  return labels[expr] || expr;
}

export default function JobsList() {
  const navigate = useNavigate();
  const [jobs, setJobs]         = useState([]);
  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState('');
  const [triggering, setTriggering] = useState({});
  const pollRef = useRef({});

  const fetchJobs = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await axios.get('/jobs');
      setJobs(res.data.data || []);
    } catch (e) {
      setError(e.response?.data?.error || 'Failed to load jobs');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchJobs();
    return () => {
      Object.values(pollRef.current).forEach(clearInterval);
    };
  }, [fetchJobs]);

  async function handleTrigger(job) {
    if (triggering[job.id]) return;
    setTriggering(t => ({ ...t, [job.id]: true }));
    try {
      await axios.post(`/jobs/${job.id}/trigger`);
      // Poll for completion
      const interval = setInterval(async () => {
        const res = await axios.get('/jobs');
        const updated = (res.data.data || []).find(j => j.id === job.id);
        if (updated) {
          const lastStatus = updated.last_run?.status;
          if (lastStatus && lastStatus !== 'running' && lastStatus !== 'pending') {
            clearInterval(interval);
            delete pollRef.current[job.id];
            setTriggering(t => { const n = { ...t }; delete n[job.id]; return n; });
            setJobs(res.data.data || []);
          } else {
            setJobs(res.data.data || []);
          }
        }
      }, 2000);
      pollRef.current[job.id] = interval;
    } catch (e) {
      alert(e.response?.data?.error || 'Failed to trigger job');
      setTriggering(t => { const n = { ...t }; delete n[job.id]; return n; });
    }
  }

  async function handleToggle(job) {
    try {
      await axios.put(`/jobs/${job.id}`, { enabled: !job.enabled });
      setJobs(prev => prev.map(j => j.id === job.id ? { ...j, enabled: !j.enabled } : j));
    } catch (e) {
      alert(e.response?.data?.error || 'Failed to update job');
    }
  }

  return (
    <div>
      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.3; }
        }
      `}</style>

      <div className="page-header">
        <h2>Scheduled Jobs</h2>
        <p>Manage background jobs, trigger them manually, and monitor execution history</p>
      </div>

      <div className="page-body">
        <div className="toolbar">
          <div className="spacer" />
          <button className="btn btn-secondary" onClick={fetchJobs} disabled={loading}>
            <IconRefresh /> Refresh
          </button>
        </div>

        {error && <div className="error-msg">{error}</div>}

        {loading && jobs.length === 0 ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: 60 }}>
            <div className="spinner" />
          </div>
        ) : jobs.length === 0 ? (
          <div className="empty-state">
            <div className="icon"><IconClock /></div>
            <h3>No jobs configured</h3>
            <p>Run the database migration to seed the default jobs</p>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            {jobs.map(job => {
              const isRunning = triggering[job.id] || job.last_run?.status === 'running';
              const lastStatus = job.last_run?.status;
              return (
                <div key={job.id} style={{
                  background: 'var(--bg-surface)',
                  border: '1px solid var(--border)',
                  borderRadius: 'var(--radius-lg)',
                  padding: '20px 24px',
                }}>
                  {/* Header row */}
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, marginBottom: 16 }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                        <button
                          className="btn btn-ghost"
                          style={{ padding: '0', color: 'var(--accent)', fontWeight: 700, fontSize: 16 }}
                          onClick={() => navigate(`/jobs/${job.id}`)}
                        >
                          {job.name}
                        </button>
                        {lastStatus && <StatusBadge status={lastStatus} />}
                        <span style={{
                          fontSize: 11, color: 'var(--text-muted)',
                          background: 'var(--bg-elevated)',
                          padding: '2px 8px', borderRadius: 999,
                        }}>
                          {job.job_type}
                        </span>
                      </div>
                      {job.description && (
                        <p style={{ color: 'var(--text-secondary)', fontSize: 13, marginTop: 4 }}>
                          {job.description}
                        </p>
                      )}
                    </div>

                    {/* Enable / disable toggle */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
                      <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                        {job.enabled ? 'Enabled' : 'Disabled'}
                      </span>
                      <ToggleSwitch
                        enabled={!!job.enabled}
                        onChange={() => handleToggle(job)}
                      />
                    </div>
                  </div>

                  {/* Stats row */}
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
                    gap: 12,
                    marginBottom: 16,
                  }}>
                    {[
                      { label: 'Schedule',    value: fmtCron(job.cron_expression) },
                      { label: 'Next Run',    value: fmtDate(job.next_run_at) },
                      { label: 'Last Run',    value: fmtDate(job.last_run_at) },
                      { label: 'Total Runs',  value: job.total_runs ?? 0 },
                      { label: 'Successes',   value: job.success_count ?? 0, color: 'var(--success)' },
                      { label: 'Failures',    value: job.failed_count ?? 0,  color: job.failed_count > 0 ? 'var(--danger)' : undefined },
                    ].map(stat => (
                      <div key={stat.label} style={{
                        background: 'var(--bg-elevated)',
                        borderRadius: 'var(--radius)',
                        padding: '10px 14px',
                      }}>
                        <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 3 }}>
                          {stat.label}
                        </div>
                        <div style={{ fontWeight: 600, fontSize: 13, color: stat.color || 'var(--text-primary)' }}>
                          {stat.value}
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Action row */}
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button
                      className="btn btn-primary"
                      style={{ fontSize: 12, display: 'flex', alignItems: 'center', gap: 6 }}
                      onClick={() => handleTrigger(job)}
                      disabled={isRunning}
                    >
                      {isRunning ? (
                        <>
                          <div className="spinner" style={{ width: 12, height: 12, borderWidth: 2 }} />
                          Running…
                        </>
                      ) : (
                        <><IconPlay /> Run Now</>
                      )}
                    </button>
                    <button
                      className="btn btn-secondary"
                      style={{ fontSize: 12 }}
                      onClick={() => navigate(`/jobs/${job.id}`)}
                    >
                      View Logs
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
